import { relevantRegionsForCategory } from '@/utils/comparisonEngine';
import type { Category, FitZoneState, RegionDelta } from '@/types/domain';

interface Props {
  category: Category;
  delta: RegionDelta;
  zones: FitZoneState;
}

const METRIC_LABEL: Record<'chest' | 'waist' | 'hip', string> = {
  chest: 'Chest/bust',
  waist: 'Waist',
  hip: 'Hip',
};

const ZONE_TEXT: Record<'tight' | 'neutral' | 'loose', string> = {
  tight: 'tighter',
  neutral: 'close match',
  loose: 'looser',
};

const ZONE_DOT: Record<'tight' | 'neutral' | 'loose', string> = {
  tight: 'bg-tight',
  neutral: 'bg-neutral',
  loose: 'bg-loose',
};

/**
 * Reads the same delta + zones the avatar shading and written explanation
 * are built from — never re-derives tight/loose independently — so this can
 * never disagree with either of them.
 */
export function MeasurementDifferences({ category, delta, zones }: Props) {
  const relevant = relevantRegionsForCategory(category);

  return (
    <dl className="space-y-3 border-t border-stone-100 pt-4">
      <dt className="sr-only">Measurement differences</dt>
      {relevant.map((metric) => {
        const zone = zones[metric];
        const value = delta[metric];
        const unavailable = zone === 'inactive' || value === null;
        return (
          <div key={metric} className="flex items-center justify-between text-base">
            <dt className="text-muted">{METRIC_LABEL[metric]}</dt>
            <dd className="flex items-center gap-2 font-medium text-ink">
              {unavailable ? (
                <span className="text-muted">Not published</span>
              ) : (
                <>
                  <span aria-hidden="true" className={`inline-block h-2 w-2 rounded-full ${ZONE_DOT[zone]}`} />
                  <span>
                    {value > 0 ? '+' : ''}
                    {value} cm ({ZONE_TEXT[zone]})
                  </span>
                </>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
